import { ThemeDrawer } from '@theme/theme-drawer';

/**
 * Custom (Miss Sophie): search drawer.
 *
 * Opens the search drawer (snippets/custom-search-drawer.liquid) from the header search buttons.
 * The drawer is Horizon's <theme-drawer>, the same component as the cart drawer; this file only
 * decides which side it slides in from and wires the predictive search to it:
 *
 * - Side: the column of the clicked search icon (header__column--left → left, --right → right),
 *   otherwise the half of the screen the icon sits in. "Drawer opens from" in the theme settings
 *   can fix it to the left or right instead.
 * - Focus: the search field, right after the drawer opens (in the click, so mobile keyboards open)
 * - Closing resets the search, like Horizon's pop-over does
 * - Cmd+K toggles the drawer, like it toggles Horizon's pop-over
 *
 * The buttons in snippets/search.liquid keep on:click="#search-modal/showDialog". The pop-over isn't
 * rendered in drawer mode, so Horizon's handler finds no element and does nothing.
 */

const DRAWER_ID = 'custom-search-drawer';

/** Header search buttons, plus anything else that opens Horizon's search pop-over */
const TRIGGER_SELECTOR = 'search-button button, [on\\:click="#search-modal/showDialog"]';

/**
 * @returns {ThemeDrawer | null}
 */
function getDrawer() {
  const drawer = document.getElementById(DRAWER_ID);

  return drawer instanceof ThemeDrawer ? drawer : null;
}

/**
 * @param {'left' | 'right'} side - Physical side
 * @returns {'start' | 'end'} Logical side, as used by the drawer CSS
 */
function toLogicalSide(side) {
  const isRtl = document.documentElement.dir === 'rtl';

  return (side === 'left') !== isRtl ? 'start' : 'end';
}

/**
 * @param {ThemeDrawer} drawer
 * @param {Element | null} trigger - The clicked search button
 * @returns {'start' | 'end'}
 */
function getSide(drawer, trigger) {
  const setting = drawer.dataset.sideSetting;

  if (setting === 'left' || setting === 'right') return toLogicalSide(setting);

  if (trigger) {
    // Header columns follow the reading direction: "left" is the inline-start column
    if (trigger.closest('.header__column--left')) return 'start';
    if (trigger.closest('.header__column--right')) return 'end';

    const rect = trigger.getBoundingClientRect();

    if (rect.width > 0) {
      return toLogicalSide(rect.left + rect.width / 2 < window.innerWidth / 2 ? 'left' : 'right');
    }
  }

  return 'end';
}

/**
 * @returns {Element | null} The search button the shopper currently sees
 */
function getVisibleTrigger() {
  return (
    Array.from(document.querySelectorAll(TRIGGER_SELECTOR)).find((element) => element.getClientRects().length > 0) ??
    null
  );
}

/**
 * @param {Element | null} trigger
 */
function openDrawer(trigger) {
  const drawer = getDrawer();

  if (!drawer) return;

  const { panel } = drawer.refs;
  const isBusy = drawer.isOpen || panel.classList.contains('theme-drawer__dialog--closing');

  // Only change the side while the drawer is fully closed, never mid-animation
  if (!isBusy) {
    drawer.dataset.side = getSide(drawer, trigger);
  }

  drawer.open();

  // theme-drawer focuses its close button on open; the search field is more useful here
  /** @type {HTMLInputElement | null} */
  const input = drawer.querySelector('input[type="search"]');
  input?.focus({ preventScroll: true });
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const trigger = event.target.closest(TRIGGER_SELECTOR);

  if (!trigger || !getDrawer()) return;

  event.preventDefault();
  openDrawer(trigger);
});

document.addEventListener('keydown', (event) => {
  // Same shortcut as Horizon's pop-over (predictive-search.js)
  if (!event.metaKey || event.key !== 'k') return;

  const drawer = getDrawer();

  if (!drawer) return;

  if (drawer.isOpen) {
    drawer.close();
  } else {
    openDrawer(getVisibleTrigger());
  }
});

document.getElementById(DRAWER_ID)?.addEventListener('theme-drawer:close', () => {
  /** @type {(HTMLElement & { resetSearch?: (keepFocus?: boolean) => void }) | null | undefined} */
  const search = getDrawer()?.querySelector('predictive-search-component');
  /** @type {HTMLInputElement | null | undefined} */
  const input = search?.querySelector('input[type="search"]');

  // Same as the pop-over: an empty field and the default results the next time it opens
  if (input?.value) {
    search?.resetSearch?.(false);
  }
});
